import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { ArrowRight, TrendingUp, Clock, Star, Users, FileText, MessageCircle, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Sidebar } from "@/components/layout/Sidebar"
import { PostCard } from "@/components/posts/PostCard"
import { getPosts, getPostsWithAuthors, enrichPostsWithTags, isNetworkFetchError } from "@/lib/db"
import { useAuth } from "@/context/AuthContext"
import type { PostWithAuthor } from "@/types"

export default function HomePage() {
  const { firebaseUser } = useAuth()
  const [trendingWithAuthors, setTrendingWithAuthors] = useState<PostWithAuthor[]>([])
  const [latestWithAuthors, setLatestWithAuthors] = useState<PostWithAuthor[]>([])
  const [topWithAuthors, setTopWithAuthors] = useState<PostWithAuthor[]>([])
  const [loading, setLoading] = useState(true)
  const isLoggedIn = !!firebaseUser

  useEffect(() => {
    async function fetchPosts() {
      try {
        const [trendingResult, latestResult, topResult] = await Promise.all([
          getPosts({ status: "published", orderByField: "upvote_count", orderDirection: "desc", limitCount: 10 }),
          getPosts({ status: "published", orderByField: "published_at", orderDirection: "desc", limitCount: 10 }),
          getPosts({ status: "published", orderByField: "view_count", orderDirection: "desc", limitCount: 10 }),
        ])

        const [trending, latest, top] = await Promise.all([
          getPostsWithAuthors(trendingResult.posts).then(enrichPostsWithTags),
          getPostsWithAuthors(latestResult.posts).then(enrichPostsWithTags),
          getPostsWithAuthors(topResult.posts).then(enrichPostsWithTags),
        ])

        setTrendingWithAuthors(trending)
        setLatestWithAuthors(latest)
        setTopWithAuthors(top)
      } catch (error) {
        if (!isNetworkFetchError(error)) {
          console.error("Error fetching posts:", error)
        }
      } finally {
        setLoading(false)
      }
    }

    fetchPosts()
  }, [])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section - only for unauthenticated visitors */}
      {!isLoggedIn && (
        <section className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-background to-indigo-50 dark:from-indigo-950/20 dark:via-background dark:to-indigo-950/20 border-b border-border">
          <div className="container mx-auto px-4 py-16 sm:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                The home for <span className="text-primary">React Native</span> developers
              </h1>
              <p className="mt-6 text-lg text-muted-foreground sm:text-xl">
                Share knowledge, learn from the community, and stay up-to-date with the latest in mobile development.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link to="/explore">
                  <Button size="lg" className="w-full sm:w-auto">
                    Explore Articles
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>

            {/* Stats */}
            <div className="mx-auto mt-16 grid max-w-4xl grid-cols-2 gap-8 sm:grid-cols-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground">10K+</div>
                <div className="mt-1 flex items-center justify-center text-sm text-muted-foreground">
                  <Users className="mr-1 h-4 w-4" />
                  Developers
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground">5K+</div>
                <div className="mt-1 flex items-center justify-center text-sm text-muted-foreground">
                  <FileText className="mr-1 h-4 w-4" />
                  Articles
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground">25K+</div>
                <div className="mt-1 flex items-center justify-center text-sm text-muted-foreground">
                  <MessageCircle className="mr-1 h-4 w-4" />
                  Comments
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-foreground">100+</div>
                <div className="mt-1 flex items-center justify-center text-sm text-muted-foreground">
                  <Star className="mr-1 h-4 w-4" />
                  Topics
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="container mx-auto px-4 py-12">
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Posts Feed */}
          <div className="flex-1">
            <Tabs defaultValue="trending" className="w-full">
              <div className="mb-6 flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="trending" className="flex items-center">
                    <TrendingUp className="mr-2 h-4 w-4" />
                    Trending
                  </TabsTrigger>
                  <TabsTrigger value="latest" className="flex items-center">
                    <Clock className="mr-2 h-4 w-4" />
                    Latest
                  </TabsTrigger>
                  <TabsTrigger value="top" className="flex items-center">
                    <Star className="mr-2 h-4 w-4" />
                    Top
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="trending" className="space-y-6">
                {trendingWithAuthors.map(post => (
                  <PostCard key={post.id} post={post} />
                ))}
              </TabsContent>

              <TabsContent value="latest" className="space-y-6">
                {latestWithAuthors.map(post => (
                  <PostCard key={post.id} post={post} />
                ))}
              </TabsContent>

              <TabsContent value="top" className="space-y-6">
                {topWithAuthors.map(post => (
                  <PostCard key={post.id} post={post} />
                ))}
              </TabsContent>
            </Tabs>

            {/* Load More */}
            <div className="mt-8 text-center">
              <Button variant="outline" size="lg">
                Load more articles
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="w-full lg:w-80">
            <Sidebar />
          </aside>
        </div>
      </section>
    </div>
  )
}
